
import Vue from 'vue';
import { db } from '@/firebase';
import { Model, RUNNITS_ACCESS_LEVEL } from '@run-diffusion/shared';
import { get$bindFirestoreOptions } from '@/mixins';
import _debounce from 'lodash/debounce';
import _trim from 'lodash/trim';
import _sortBy from 'lodash/sortBy';
import { mapState, mapGetters, mapActions } from 'vuex';
import RunnitModelsDialog from '@/views/Runnits/base/RunnitModelsDialog.vue';
import RunnitImage from '@/views/Runnits/RunnitImage.vue';
import GlassButton from '@/components/base/GlassButton.vue';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { RUNNITS_OWNER_SELECTION } from '@/views/Runnits/constants';

export default Vue.extend({
	name: 'ModelSuggestionMenu',
	components: {
		RunnitModelsDialog,
		RunnitImage,
		GlassButton,
	},
	props: {
		value: { type: Boolean, default: false },
		position: { type: Object, default: () => ({ x: 0, y: 0 }) },
		modelArchitecture: { type: String, required: true },
	},
	data () {
		return {
			RUNNITS_ACCESS_LEVEL,
			RUNNITS_OWNER_SELECTION,
			isOpen: false,
			MODEL_NAME_TRUNC_LENGTH: 60,
			Z_INDEX: 1001,
			modelsPrivate: [] as Model[],
			modelsShared: [] as Model[],
			selectedIndex: 0,
			isSearchingModels: false,
			positionX: 0,
			positionY: 0,
			nudgeRight: 0,
			nudgeLeft: 0,
			debouncedSearchModels: null as Function,
			isDeboundingSearchModels: false,
			isModelBrowserOpen: false,
			lastSearchQuery: '',
			localSearchQuery: '',
			browseModelsBtnIsActive: false,
		};
	},
	computed: {
		...mapState([
			'user',
			'team',
			'runnitState',
		]),
		...mapGetters([
			'displayLabels',
		]),
		filteredModels (): Model[] {
			const searchValue: string = _trim(this.localSearchQuery);
			// Combine private and shared models
			const combinedModels: Model[] = [...this.modelsPrivate, ...this.modelsShared];

			if (!searchValue) {
				// When no search text, keep the order from the database query (createdAt desc)
				return combinedModels;
			}

			return _sortBy(
				combinedModels.filter((model: Model) => {
					// Filter by model architecture if specified
					if (this.modelArchitecture && model.architecture !== this.modelArchitecture) {
						return false;
					}

					// Filter out models without a file
					if (!model.file) {
						return false;
					}

					// Filter by search text
					const searchLower: string = searchValue.toLowerCase();
					return (model.nameLowerCase || model.name || '').includes(searchLower);
				}),
				'name',
			);
		},
	},
	watch: {
		value (newVal: boolean) {
			this.isOpen = newVal;
			if (newVal) {
				this.updatePosition(this.position);
				// Focus the chip on next tick after it's rendered
				this.$nextTick(() => {
					this.focusChip();
				});
			}
		},
		isOpen (newVal: boolean, oldVal: boolean) {
			if (this.value !== newVal) {
				this.$emit('input', newVal);
			}

			if (newVal && !oldVal) {
				// Load models when menu first opens
				this.onDebouncedSearchModels(true);
			} else {
				// Reset selection when menu closes
				this.selectedIndex = 0;
			}
		},
		localSearchQuery: {
			immediate: true,
			handler (newVal: string, oldVal: string) {
				if (this.isOpen && newVal !== oldVal) {
					// Only trigger the debounced search if the menu is open
					this.onDebouncedSearchModels(false);
					// Reset selection when search query changes
					this.selectedIndex = 0;
				}
			},
		},
		position: {
			handler (newVal: { x: number, y: number }) {
				this.updatePosition(newVal);
			},
			deep: true,
		},
	},
	created () {
		this.isOpen = this.value;
	},
	methods: {
		...mapActions([
			'updateSnackbar',
			'updateRunnitState',
		]),
		onDebouncedSearchModels (isFirstSearch: boolean = false) {
			if (isFirstSearch) {
				if (this.debouncedSearchModels && this.debouncedSearchModels.cancel) {
					this.debouncedSearchModels.cancel();
				}
				this.debouncedSearchModels = _debounce(this.loadModels, 1);
			} else {
				if (this.debouncedSearchModels && this.debouncedSearchModels.cancel) {
					this.debouncedSearchModels.cancel();
				}
				this.debouncedSearchModels = _debounce(this.loadModels, 1000);
			}
			this.isDeboundingSearchModels = true;
			this.debouncedSearchModels();
		},
		updatePosition (position: { x: number, y: number }) {
			this.positionX = (position?.x || 0) + 15;
			this.positionY = (position?.y || 0) + 58;

			// Calculate nudge values to prevent menu from going off-screen
			const menuWidth: number = 300; // max-width value
			const viewportWidth: number = window.innerWidth;

			// Get the element this menu is attached to (the parent component)
			const parentElement: HTMLElement = this.$el.parentElement as HTMLElement;
			let parentLeft: number = 0;

			if (parentElement) {
				const parentRect: DOMRect = parentElement.getBoundingClientRect();
				parentLeft = parentRect.left;
			}

			// Calculate absolute position considering parent's position
			const absolutePositionX: number = parentLeft + this.positionX;
			const ADD_PADDING: number = 10;
			if (absolutePositionX + menuWidth > viewportWidth) {
				// Menu would go off the right edge
				this.nudgeRight = -((absolutePositionX + menuWidth) - viewportWidth + ADD_PADDING);
			} else if (absolutePositionX < 0) {
				// Menu would go off the left edge
				this.nudgeLeft = -(Math.abs(absolutePositionX) + ADD_PADDING);
				this.nudgeRight = 0;
			} else {
				this.nudgeRight = 0;
			}
		},
		async loadModels () {
			this.isDeboundingSearchModels = false;
			if (!this.user) return;

			this.isSearchingModels = true;
			this.lastSearchQuery = _trim(this.localSearchQuery);

			const teamId: string = this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM && this.team ? this.team.id : null;

			// Create base queries for both private and shared models
			if (teamId) {
				// Load private models (user-specific)
				let privateModelsRef = db.collection('models')
					.where('teamId', '==', teamId)
					.where('accessLevel', '==', RUNNITS_ACCESS_LEVEL.PRIVATE)
					.where('architecture', '==', this.modelArchitecture)
					.where('isDeleted', '==', false)
					.where('userId', '==', this.user.id);

				// Load shared models (team-wide)
				let sharedModelsRef = db.collection('models')
					.where('teamId', '==', teamId)
					.where('accessLevel', '==', RUNNITS_ACCESS_LEVEL.SHARED)
					.where('architecture', '==', this.modelArchitecture)
					.where('isDeleted', '==', false);

				// Add name filter for both queries if search text is provided
				if (this.lastSearchQuery) {
					const startValue: string = this.lastSearchQuery.toLowerCase();
					const endValue: string = startValue.replace(/.$/, c => String.fromCharCode(c.charCodeAt(0) + 1));

					// index created: models: accessLevel Ascending architecture Ascending isDeleted Ascending teamId Ascending userId Ascending nameLowerCase Ascending __name__ Ascending
					privateModelsRef = privateModelsRef
						.where('nameLowerCase', '>=', startValue)
						.where('nameLowerCase', '<', endValue)
						.orderBy('nameLowerCase', 'asc');

					// index created: models: accessLevel Ascending architecture Ascending isDeleted Ascending teamId Ascending nameLowerCase Ascending __name__ Ascending
					sharedModelsRef = sharedModelsRef
						.where('nameLowerCase', '>=', startValue)
						.where('nameLowerCase', '<', endValue)
						.orderBy('nameLowerCase', 'asc');
				} else {
					// If no search text, order by createdAt in descending order
					// index created: models: accessLevel Ascending architecture Ascending isDeleted Ascending teamId Ascending userId Ascending createdAt Descending __name__ Descending
					privateModelsRef = privateModelsRef.orderBy('createdAt', 'desc');

					// index created: models: accessLevel Ascending architecture Ascending isDeleted Ascending teamId Ascending createdAt Descending __name__ Descending
					sharedModelsRef = sharedModelsRef.orderBy('createdAt', 'desc');
				}

				// Add limit to both queries
				privateModelsRef = privateModelsRef.limit(10);
				sharedModelsRef = sharedModelsRef.limit(10);

				try {
					// Bind both result sets
					await Promise.all([
						this.$bind(
							'modelsPrivate',
							privateModelsRef,
							get$bindFirestoreOptions(),
						),
						this.$bind(
							'modelsShared',
							sharedModelsRef,
							get$bindFirestoreOptions(),
						)
					]);
					this.isSearchingModels = false;
					this.selectedIndex = 0;
				} catch (e) {
					console.error('Error loading models', e);
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: 'Failed to load models. Please try again.',
						show: true,
					});
					this.isSearchingModels = false;
				}
			} else {
				// Personal models only (no team context)
				let modelsRef = db.collection('models')
					.where('userId', '==', this.user.id)
					.where('teamId', '==', null)
					.where('architecture', '==', this.modelArchitecture)
					.where('isDeleted', '==', false);

				// Add name filter if search text is provided
				if (this.lastSearchQuery) {
					// index created: models: architecture Ascending isDeleted Ascending teamId Ascending userId Ascending nameLowerCase Ascending __name__ Ascending
					const startValue: string = this.lastSearchQuery.toLowerCase();
					const endValue: string = startValue.replace(/.$/, c => String.fromCharCode(c.charCodeAt(0) + 1));
					modelsRef = modelsRef
						.where('nameLowerCase', '>=', startValue)
						.where('nameLowerCase', '<', endValue)
						.orderBy('nameLowerCase', 'asc');
				} else {
					// If no search text, order by createdAt in descending order
					// index created: models: architecture Ascending isDeleted Ascending teamId Ascending userId Ascending createdAt Descending __name__ Descending
					modelsRef = modelsRef.orderBy('createdAt', 'desc');
				}

				// Add limit
				modelsRef = modelsRef.limit(10);

				try {
					// Bind private models and reset shared models
					await this.$bind(
						'modelsPrivate',
						modelsRef,
						get$bindFirestoreOptions(),
					);
					// Clear shared models when not in team context
					this.modelsShared = [];
					this.isSearchingModels = false;
					this.selectedIndex = 0;
				} catch (e) {
					console.error('Error loading models', e);
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: 'Failed to load models. Please try again.',
						show: true,
					});
					this.isSearchingModels = false;
				}
			}
		},
		selectNextModel () {
			if (this.filteredModels.length === 0) return;
			// Add 1 to max index to account for "Browse models" button
			const maxIndex: number = this.filteredModels.length;
			// If we're at the last model, go to the "Browse models" button
			if (this.selectedIndex === maxIndex - 1) {
				this.selectedIndex = maxIndex;
				this.browseModelsBtnIsActive = true;
			} else if (this.selectedIndex === maxIndex) {
				// If we're at the browse button, do nothing (no wrapping)
				return;
			} else {
				// Normal case, go to next model
				this.selectedIndex = this.selectedIndex + 1;
				this.browseModelsBtnIsActive = false;
				this.scrollToSelected();
			}
		},
		selectPrevModel () {
			if (this.filteredModels.length === 0) return;
			// Add 1 to max index to account for "Browse models" button
			const maxIndex: number = this.filteredModels.length;
			// If we're at the first model, do nothing (no wrapping)
			if (this.selectedIndex === 0) {
				return;
			} else if (this.selectedIndex === maxIndex) {
				// If we're at the browse button, go to last model
				this.selectedIndex = maxIndex - 1;
				this.browseModelsBtnIsActive = false;
				this.scrollToSelected();
			} else {
				// Normal case
				this.selectedIndex = this.selectedIndex - 1;
				this.browseModelsBtnIsActive = false;
				this.scrollToSelected();
			}
		},
		scrollToSelected () {
			this.$nextTick(() => {
				const modelList: HTMLElement = this.$refs.modelList as HTMLElement;
				const activeItem: HTMLElement = modelList?.querySelector('.active-suggestion-item') as HTMLElement;

				if (modelList && activeItem) {
					this.$scrollTo(activeItem, 100, {
						container: modelList,
					});
				}
			});
		},
		onEnterKeySelectModel () {
			if (this.filteredModels.length === 0) return;
			// If selected index is equal to filteredModels.length, it means we have the "Browse models" button selected
			if (this.selectedIndex === this.filteredModels.length) {
				this.openModelBrowser();
			} else {
				this.selectModel(this.filteredModels[this.selectedIndex]);
			}
		},
		selectModel (model: Model) {
			this.$emit('select', model);
			if (model && model.accessLevel !== this.runnitState.runnitsAccessLevel) {
				this.updateRunnitState({
					runnitsAccessLevel: model.accessLevel,
				});
			}
			this.close();
		},
		close () {
			this.isOpen = false;
			this.$emit('on-close');
		},
		openModelBrowser () {
			this.isModelBrowserOpen = true;
		},
		onModelBrowserSelect (model: Model) {
			this.isModelBrowserOpen = false;
			this.selectModel(model);
		},
		focusChip () {
			// Focus the editable chip and place cursor at the end
			const chip: HTMLElement = this.$refs.editableChip as HTMLElement;
			chip?.focus();
		},

		onChipKeydown (event: KeyboardEvent) {
			// Handle keyboard navigation
			if (event.key === 'ArrowDown') {
				event.preventDefault();
				this.selectNextModel();
			} else if (event.key === 'ArrowUp') {
				event.preventDefault();
				this.selectPrevModel();
			} else if (event.key === 'Enter') {
				event.preventDefault();
				this.onEnterKeySelectModel();
			} else if (event.key === 'Escape') {
				event.preventDefault();
				this.close();
			} else if (event.key === 'Backspace' && this.localSearchQuery === '') {
				// If backspace is pressed when input is already empty, close the menu
				event.preventDefault();
				this.close();
			}
		},
		// Highlight matching text in model names
		highlightMatchingText (text: string, query: string): string {
			const cleanQuery: string = _trim(query.toLowerCase());

			// First truncate the text to avoid HTML tags being counted in length
			const truncatedText: string = this.$options.filters.truncate(text, { length: this.MODEL_NAME_TRUNC_LENGTH });

			if (!cleanQuery) return truncatedText;

			// Safely escape the text for HTML
			const safeText: string = truncatedText.replace(/[&<>"']/g, char => {
				const entities: Record<string, string> = {
					'&': '&amp;',
					'<': '&lt;',
					'>': '&gt;',
					'"': '&quot;',
					"'": '&#39;'
				};
				return entities[char];
			});

			// Find the matching part (case insensitive)
			const index: number = safeText.toLowerCase().indexOf(cleanQuery);
			if (index === -1) return safeText;

			// Split the text into parts: before match, match, after match
			const before: string = safeText.substring(0, index);
			const match: string = safeText.substring(index, index + cleanQuery.length);
			const after: string = safeText.substring(index + cleanQuery.length);

			// Create HTML with highlighted part
			return `${before}<span style="background-color: var(--grey-600); padding: 2px 0;">${match}</span>${after}`;
		},
	},
});
